import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { KeyRound, Loader2, LogOut, Users, Mail, CalendarDays } from "lucide-react";
import Mascot from "@/components/Mascot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "vault_keeper_token";

const VaultKeeper = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/drop-alerts`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setData(null);
      } else {
        toast.error("Couldn't load the ledger. Try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Vault Keeper — Aardvarks Vault";
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
      setPassword("");
      await load(res.data.token);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Sign-in failed.");
    } finally {
      setLoading(false);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="mx-auto max-w-4xl px-5 pb-24 pt-40 sm:px-8" data-testid="vault-keeper-page">
      <div className="flex items-center gap-4">
        <Mascot size={52} />
        <div>
          <p className="eyebrow">Private · Vault Keeper Only</p>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-cream sm:text-4xl" data-testid="vault-keeper-title">
            The Drop-Alert Ledger
          </h1>
        </div>
      </div>

      {!token ? (
        <form onSubmit={onLogin} className="card-frame mx-auto mt-14 max-w-md p-8 text-center" data-testid="vault-keeper-login" noValidate>
          <div className="mx-auto w-fit" aria-hidden="true">
            <div className="keyhole" />
          </div>
          <h2 className="mt-5 font-serif text-xl font-semibold text-cream">Present the vault passphrase</h2>
          <p className="mt-2 text-xs text-cream/55">This page is unlisted and passphrase-protected.</p>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Passphrase"
            aria-label="Admin passphrase"
            className="field mt-6"
            data-testid="vault-keeper-password-input"
          />
          {error && <p className="mt-3 text-xs text-red-400" data-testid="vault-keeper-error">{error}</p>}
          <button type="submit" className="btn-gold mt-6 w-full" disabled={loading} data-testid="vault-keeper-login-button">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={15} />}
            {loading ? "Checking…" : "Open the Ledger"}
          </button>
        </form>
      ) : (
        <div className="mt-12" data-testid="vault-keeper-ledger">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3 rounded-full border border-mint/30 bg-mint/5 px-5 py-2.5">
              <Users size={16} className="text-mint" />
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-cream/80" data-testid="vault-keeper-count">
                {data ? `${data.count} subscriber${data.count === 1 ? "" : "s"}` : "Loading…"}
              </span>
            </div>
            <div className="flex gap-3">
              <button onClick={() => load(token)} className="btn-ghost !px-5 !py-2" data-testid="vault-keeper-refresh-button">
                Refresh
              </button>
              <button onClick={onLogout} className="btn-ghost !border-red-400/50 !px-5 !py-2 !text-red-300 hover:!bg-red-400/10" data-testid="vault-keeper-logout-button">
                <LogOut size={14} /> Lock the Vault
              </button>
            </div>
          </div>

          <div className="card-frame mt-8 overflow-hidden" data-testid="vault-keeper-table">
            {loading && !data ? (
              <p className="p-10 text-center text-sm text-cream/60">Opening the ledger…</p>
            ) : data && data.subscribers.length === 0 ? (
              <p className="p-10 text-center text-sm text-cream/60" data-testid="vault-keeper-empty">
                No subscribers yet — the first treasure hunter will appear here.
              </p>
            ) : (
              <ul className="divide-y divide-cream/10">
                {data?.subscribers.map((s, i) => (
                  <li key={s.email} className="flex flex-col gap-2 px-6 py-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`vault-keeper-row-${i}`}>
                    <div className="flex items-center gap-3">
                      <Mail size={15} className="shrink-0 text-gold" />
                      <div>
                        <p className="text-sm font-medium text-cream">{s.email}</p>
                        {s.name && <p className="text-xs text-cream/55">{s.name}</p>}
                      </div>
                    </div>
                    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-mint/80">
                      <CalendarDays size={12} /> {fmtDate(s.created_at)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VaultKeeper;
